import { motion } from 'framer-motion';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  UserCircle,
  Brain,
  Code,
  ArrowRight,
  Info,
  Clock,
} from 'lucide-react';
import { useCurrentLang } from '@/lib/hooks/useCurrentLang';
import { Link } from 'react-router-dom';

interface GetStartedModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GetStartedModal({ open, onOpenChange }: GetStartedModalProps) {
  const currentLang = useCurrentLang();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl bg-white dark:bg-gray-900 p-0">
        <DialogHeader className="px-6 pt-5 pb-2">
          <DialogTitle className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            How would you like to get started?
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Choose the path that best fits your needs
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-2 px-6 pb-5 mt-2">
          {/* Learn More about bunker.ai */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05, duration: 0.3 }}
            whileHover={{ scale: 1.02 }}
            className="cursor-pointer"
          >
            <Link to={`/${currentLang}/bunker`} onClick={() => onOpenChange(false)}>
              <Card className="border-2 border-blue-500 hover:border-blue-600 dark:hover:border-blue-400 transition-all duration-300 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 shadow-md hover:shadow-lg">
                <CardHeader className="pb-2">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30 shrink-0">
                      <Info className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-lg text-gray-900 dark:text-white">
                        Mehr über bunker.ai erfahren
                      </CardTitle>
                      <CardDescription className="text-sm mt-1 text-gray-600 dark:text-gray-400">
                        Erfahren Sie, wie bunker.ai Ihre Daten schützt und Produktivität steigert
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 pb-2">
                  <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white h-9 text-sm">
                    <Info className="mr-2 w-3 h-3" />
                    Mehr erfahren
                    <ArrowRight className="ml-2 w-3 h-3" />
                  </Button>
                </CardContent>
              </Card>
            </Link>
          </motion.div>

          {/* Divider */}
          <div className="relative my-1">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-gray-300 dark:border-gray-700" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white dark:bg-gray-900 px-2 text-gray-500">More Options</span>
            </div>
          </div>

          {/* Option 1: Demo Account - COMING SOON */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.3 }}
          >
            <Card className="border-2 border-gray-300 dark:border-gray-700 transition-all duration-300 bg-gray-50 dark:bg-gray-800/50 opacity-70">
              <CardHeader className="pb-2">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-gray-200 dark:bg-gray-700 shrink-0">
                    <UserCircle className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-lg text-gray-500 dark:text-gray-400">
                        Create Demo Account
                      </CardTitle>
                      <span className="px-2 py-0.5 text-xs font-semibold bg-gray-400 text-white rounded-full flex items-center gap-1 shrink-0">
                        <Clock className="w-3 h-3" />
                        Coming Soon
                      </span>
                    </div>
                    <CardDescription className="text-sm mt-1 text-gray-500 dark:text-gray-500">
                      Access our materials, presentations, and explore platform
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0 pb-2">
                <Button className="w-full bg-gray-400 text-gray-600 dark:bg-gray-700 dark:text-gray-500 cursor-not-allowed h-9 text-sm" disabled>
                  Sign Up Free
                  <ArrowRight className="ml-2 w-3 h-3" />
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* Option 2: Tech Team Workshop - COMING SOON */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.3 }}
          >
            <Card className="border-2 border-gray-300 dark:border-gray-700 transition-all duration-300 bg-gray-50 dark:bg-gray-800/50 opacity-70">
              <CardHeader className="pb-2">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-gray-200 dark:bg-gray-700 shrink-0">
                    <Brain className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-lg text-gray-500 dark:text-gray-400">
                        Book AI Workshop for Tech Team
                      </CardTitle>
                      <span className="px-2 py-0.5 text-xs font-semibold bg-gray-400 text-white rounded-full flex items-center gap-1 shrink-0">
                        <Clock className="w-3 h-3" />
                        Coming Soon
                      </span>
                    </div>
                    <CardDescription className="text-sm mt-1 text-gray-500 dark:text-gray-500">
                      Learn how to use AI in your company safely and efficiently
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0 pb-2">
                <Button className="w-full bg-gray-400 text-gray-600 dark:bg-gray-700 dark:text-gray-500 cursor-not-allowed h-9 text-sm" disabled>
                  Book Workshop
                  <ArrowRight className="ml-2 w-3 h-3" />
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* Option 3: Developer Workshop - COMING SOON */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.3 }}
          >
            <Card className="border-2 border-gray-300 dark:border-gray-700 transition-all duration-300 bg-gray-50 dark:bg-gray-800/50 opacity-70">
              <CardHeader className="pb-2">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-gray-200 dark:bg-gray-700 shrink-0">
                    <Code className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-lg text-gray-500 dark:text-gray-400">
                        Book AI Tools Workshop for Developers
                      </CardTitle>
                      <span className="px-2 py-0.5 text-xs font-semibold bg-gray-400 text-white rounded-full flex items-center gap-1 shrink-0">
                        <Clock className="w-3 h-3" />
                        Coming Soon
                      </span>
                    </div>
                    <CardDescription className="text-sm mt-1 text-gray-500 dark:text-gray-500">
                      Learn do's and don'ts and how to use AI tools to enhance productivity
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0 pb-2">
                <Button className="w-full bg-gray-400 text-gray-600 dark:bg-gray-700 dark:text-gray-500 cursor-not-allowed h-9 text-sm" disabled>
                  Book Workshop
                  <ArrowRight className="ml-2 w-3 h-3" />
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
